import React, { useState, useEffect } from 'react';
import {
  Building2, Users, RefreshCw, Eye, ChevronDown, ChevronRight
} from 'lucide-react';
import { getAllOrganizations, getOrgMembers } from '../../services/superAdminService';

const ImpersonationPanel = ({ onImpersonate }) => {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrg, setExpandedOrg] = useState(null);
  const [orgMembers, setOrgMembers] = useState({});
  const [loadingMembers, setLoadingMembers] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const allOrgs = await getAllOrganizations();
        setOrgs(allOrgs);
      } catch (error) {
        console.error('Error loading orgs:', error);
      }
      setLoading(false);
    };
    load();
  }, []);

  const handleExpandOrg = async (orgId) => {
    if (expandedOrg === orgId) {
      setExpandedOrg(null);
      return;
    }
    setExpandedOrg(orgId);

    if (!orgMembers[orgId]) {
      setLoadingMembers(orgId);
      try {
        const members = await getOrgMembers(orgId);
        setOrgMembers(prev => ({ ...prev, [orgId]: members }));
      } catch (error) {
        console.error('Error loading members:', error);
      }
      setLoadingMembers(null);
    }
  };

  const handleImpersonate = (org, member) => {
    if (onImpersonate) {
      onImpersonate({
        orgId: org.id,
        orgName: org.name,
        memberName: `${member.firstName || ''} ${member.lastName || ''}`.trim() || member.email,
        memberEmail: member.email,
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-6 h-6 animate-spin" style={{ color: '#516469' }} />
        <span className="ml-2" style={{ color: '#516469' }}>Loading organizations...</span>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-lg font-bold" style={{ color: '#071D39' }}>Account Impersonation</h2>
        <p className="text-sm mt-1" style={{ color: '#516469' }}>
          Select an organization, then choose a member to view their account. This does not count as a login or use a seat.
        </p>
      </div>

      <div className="space-y-3">
        {orgs.map((org) => {
          const isExpanded = expandedOrg === org.id;
          const members = orgMembers[org.id] || [];
          const isLoadingThis = loadingMembers === org.id;
          const planColors = {
            trial:     { bg: '#FEFCBF', text: '#D69E2E' },
            active:    { bg: '#C6F6D5', text: '#2F855A' },
            founding:  { bg: '#D4AF3720', text: '#D4AF37' },
            cancelled: { bg: '#FED7D7', text: '#E53E3E' },
          };
          const planStyle = planColors[org.plan] || planColors.trial;

          return (
            <div key={org.id} className="bg-white rounded-lg shadow overflow-hidden">
              <button
                onClick={() => handleExpandOrg(org.id)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  {isExpanded
                    ? <ChevronDown className="w-4 h-4" style={{ color: '#516469' }} />
                    : <ChevronRight className="w-4 h-4" style={{ color: '#516469' }} />
                  }
                  <Building2 className="w-5 h-5" style={{ color: '#071D39' }} />
                  <span className="font-semibold" style={{ color: '#071D39' }}>{org.name}</span>
                  <span
                    className="px-2 py-0.5 rounded text-xs font-semibold"
                    style={{ backgroundColor: planStyle.bg, color: planStyle.text }}
                  >
                    {org.plan}
                  </span>
                </div>
                <span className="text-xs font-mono" style={{ color: '#89A8B1' }}>{org.id}</span>
              </button>

              {isExpanded && (
                <div className="border-t" style={{ borderColor: '#E2E8F0' }}>
                  {isLoadingThis ? (
                    <div className="px-4 py-3 flex items-center gap-2">
                      <RefreshCw className="w-4 h-4 animate-spin" style={{ color: '#516469' }} />
                      <span className="text-sm" style={{ color: '#516469' }}>Loading members...</span>
                    </div>
                  ) : members.length === 0 ? (
                    <div className="px-4 py-3">
                      <span className="text-sm" style={{ color: '#89A8B1' }}>No members found</span>
                    </div>
                  ) : (
                    members.map((member) => (
                      <div
                        key={member.id}
                        className="flex items-center justify-between px-4 py-3 border-t"
                        style={{ borderColor: '#F0F0F0', backgroundColor: '#FAFAFA' }}
                      >
                        <div className="flex items-center gap-3 pl-8">
                          <Users className="w-4 h-4" style={{ color: '#89A8B1' }} />
                          <div>
                            <span className="text-sm font-semibold" style={{ color: '#071D39' }}>
                              {member.firstName} {member.lastName}
                            </span>
                            <span className="text-xs ml-2" style={{ color: '#89A8B1' }}>
                              {member.email}
                            </span>
                            <span
                              className="ml-2 px-1.5 py-0.5 rounded text-xs"
                              style={{ backgroundColor: '#E2E8F0', color: '#516469' }}
                            >
                              {member.role}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => handleImpersonate(org, member)}
                          className="flex items-center gap-1 px-3 py-1.5 rounded text-sm font-semibold transition-colors hover:opacity-90"
                          style={{ backgroundColor: '#071D39', color: '#FFFFFF' }}
                        >
                          <Eye className="w-3.5 h-3.5" />
                          View As
                        </button>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {orgs.length === 0 && (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <Building2 className="w-12 h-12 mx-auto mb-4" style={{ color: '#89A8B1' }} />
          <p style={{ color: '#516469' }}>No organizations found.</p>
        </div>
      )}
    </div>
  );
};

export default ImpersonationPanel;